<template>
  <div class="pipeline-analytics">
    <header class="dashboard-header">
      <div class="header-content">
        <div class="logo-section">
          <div class="kpmg-logo" @click="navigateToHome">
            <img src="/kpmg-logo-blue.svg" alt="KPMG" class="kpmg-logo-img" />
          </div>
          <h1>Pipeline Analytics</h1>
        </div>
        <button @click="goBack" class="back-btn">← Back to Dashboard</button>
      </div>
    </header>

    <main class="main-content">
      <div class="analytics-layout">
        
        <!-- Left Navigation Panel -->
        <nav class="left-navigation">
          <ul class="nav-tabs">
            <li 
              v-for="tab in navigationTabs" 
              :key="tab.id"
              :class="['nav-tab', { 'active': activeTab === tab.id }]"
              @click="setActiveTab(tab.id)"
            >
              <span class="tab-icon">{{ tab.icon }}</span>
              <span class="tab-text">{{ tab.name }}</span>
            </li>
          </ul>
        </nav>

        <!-- Main Content Area -->
        <div class="content-area">
          <div class="content-container" :class="{ 'fade-in': contentLoaded }">
            
            <!-- Dashboard Tab Content -->
            <div v-if="activeTab === 'dashboard'" class="dashboard-content">
              
              <!-- Left Metrics Column -->
              <div class="metrics-section">
                <div class="metrics-grid">
                  <div v-for="metric in dashboardMetrics" :key="metric.id" class="metric-card">
                    <div class="metric-icon">{{ metric.icon }}</div>
                    <div class="metric-content">
                      <div class="metric-value">{{ metric.value }}</div>
                      <div class="metric-label">{{ metric.label }}</div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Charts Section -->
              <div class="charts-section">
                
                <!-- Top Charts Row -->
                <div class="charts-row">
                  
                  <!-- Portfolio Size by Stage -->
                  <div class="chart-card">
                    <div class="chart-header">
                      <h3>PORTFOLIO SIZE BY STAGE</h3>
                    </div>
                    <div class="chart-content">
                      <div class="pie-chart">
                        <div class="pie-center">
                          <span class="pie-total">{{ totalProjects }}</span>
                          <span class="pie-label">Projects</span>
                        </div>
                        <div class="pie-segments">
                          <div v-for="(segment, index) in portfolioStageData" :key="segment.stage" 
                               class="pie-segment" 
                               :style="{ 
                                 '--segment-color': segment.color,
                                 '--segment-percentage': segment.percentage,
                                 '--segment-offset': getSegmentOffset(index)
                               }">
                          </div>
                        </div>
                      </div>
                      <div class="pie-legend">
                        <div v-for="segment in portfolioStageData" :key="segment.stage" class="legend-item">
                          <span class="legend-dot" :style="{ backgroundColor: segment.color }"></span>
                          <span class="legend-text">{{ segment.stage }} ({{ segment.count }})</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Payment Source -->
                  <div class="chart-card">
                    <div class="chart-header">
                      <h3>PAYMENT SOURCE</h3>
                    </div>
                    <div class="chart-content">
                      <div class="bar-chart">
                        <div v-for="item in paymentSourceData" :key="item.source" class="bar-item">
                          <div class="bar-container">
                            <div class="bar-fill" :style="{ 
                              width: (item.count / maxPaymentSource * 100) + '%',
                              backgroundColor: '#0056b3'
                            }"></div>
                          </div>
                          <div class="bar-labels">
                            <span class="bar-label">{{ item.source }}</span>
                            <span class="bar-value">{{ item.count }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Type of Project -->
                  <div class="chart-card">
                    <div class="chart-header">
                      <h3>TYPE OF PROJECT</h3>
                    </div>
                    <div class="chart-content">
                      <div class="bar-chart">
                        <div v-for="item in projectTypeData" :key="item.type" class="bar-item">
                          <div class="bar-container">
                            <div class="bar-fill" :style="{ 
                              width: (item.count / maxProjectType * 100) + '%',
                              backgroundColor: '#0056b3'
                            }"></div>
                          </div>
                          <div class="bar-labels">
                            <span class="bar-label">{{ item.type }}</span>
                            <span class="bar-value">{{ item.count }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                </div>

                <!-- Bottom Charts Row -->
                <div class="charts-row">
                  
                  <!-- PSAT Score Gauge -->
                  <div class="chart-card">
                    <div class="chart-header">
                      <h3>PSAT SCORE</h3>
                    </div>
                    <div class="chart-content">
                      <div class="gauge-chart">
                        <div class="gauge-container">
                          <div class="gauge-arc" :style="{ 
                            background: `conic-gradient(#0056b3 ${averagePSATScore * 3.6}deg, #e0e0e0 0deg)`
                          }">
                            <div class="gauge-center">
                              <span class="gauge-value">{{ averagePSATScore.toFixed(1) }}</span>
                              <span class="gauge-label">Average Score</span>
                            </div>
                          </div>
                        </div>
                        <div class="gauge-legend">
                          <div class="gauge-range">
                            <span>0</span>
                            <span>50</span>
                            <span>100</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Sector Distribution -->
                  <div class="chart-card">
                    <div class="chart-header">
                      <h3>PROJECTS BY SECTOR</h3>
                    </div>
                    <div class="chart-content">
                      <div class="vertical-bar-chart">
                        <div v-for="item in sectorData" :key="item.sector" class="vertical-bar-item">
                          <div class="vertical-bar-container">
                            <div class="vertical-bar-fill" :style="{ 
                              height: (item.count / maxSectorCount * 100) + '%',
                              backgroundColor: '#0056b3'
                            }"></div>
                          </div>
                          <div class="vertical-bar-label">{{ item.sector }}</div>
                          <div class="vertical-bar-value">{{ item.count }}</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Investment Size Distribution -->
                  <div class="chart-card">
                    <div class="chart-header">
                      <h3>INVESTMENT SIZE (USD M)</h3>
                    </div>
                    <div class="chart-content">
                      <div class="vertical-bar-chart">
                        <div v-for="item in investmentSizeData" :key="item.range" class="vertical-bar-item">
                          <div class="vertical-bar-container">
                            <div class="vertical-bar-fill" :style="{ 
                              height: (item.count / maxInvestmentCount * 100) + '%',
                              backgroundColor: '#0056b3'
                            }"></div>
                          </div>
                          <div class="vertical-bar-label">{{ item.range }}</div>
                          <div class="vertical-bar-value">{{ item.count }}</div>
                        </div>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </div>

            <!-- Climate Change Resilience Tab -->
            <div v-else-if="activeTab === 'climate-resilience'" class="climate-resilience-content">
              
              <!-- Section 1: Climate Resilience Profile - Portfolio -->
              <div class="chart-card">
                <div class="chart-header">
                  <h3>CLIMATE RESILIENCE PROFILE - PORTFOLIO</h3>
                </div>
                <div class="chart-content">
                  <div class="gauge-chart">
                    <div class="resilience-gauge-container">
                      <div class="resilience-gauge-arc">
                        <div class="gauge-segments">
                          <div v-for="segment in resilienceScoreData" :key="segment.label" 
                               class="gauge-segment" 
                               :style="{ 
                                 '--segment-color': segment.color,
                                 '--segment-count': segment.count,
                                 '--segment-angle': getResilienceSegmentAngle(segment.count)
                               }">
                          </div>
                        </div>
                        <div class="gauge-center">
                          <span class="gauge-value">{{ totalResilienceProjects }}</span>
                          <span class="gauge-label">Total Projects</span>
                        </div>
                      </div>
                    </div>
                    <div class="resilience-legend">
                      <div v-for="segment in resilienceScoreData" :key="segment.label" class="legend-item">
                        <span class="legend-dot" :style="{ backgroundColor: segment.color }"></span>
                        <span class="legend-text">{{ segment.label }}: {{ segment.count }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Section 2: Climate Resilience Profile - Sector with High Scores -->
              <div class="chart-card">
                <div class="chart-header">
                  <h3>CLIMATE RESILIENCE PROFILE - SECTOR WITH HIGH SCORES</h3>
                </div>
                <div class="chart-content">
                  <div class="horizontal-bar-chart">
                    <div v-for="item in sectorResilienceData" :key="item.sector" class="horizontal-bar-item">
                      <div class="bar-labels">
                        <span class="bar-label">{{ item.sector }}</span>
                      </div>
                      <div class="bar-container">
                        <div class="bar-fill" :style="{ 
                          width: (item.score / maxSectorResilienceScore * 100) + '%',
                          backgroundColor: '#0056b3'
                        }"></div>
                      </div>
                      <div class="bar-value-container">
                        <span class="bar-value">{{ item.score }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Section 3: Climate-Smart PPP Suitability -->
              <div class="chart-card">
                <div class="chart-header">
                  <h3>CLIMATE-SMART PPP SUITABILITY</h3>
                </div>
                <div class="chart-content">
                  <div class="ppp-suitability-chart">
                    <div v-for="item in pppSuitabilityData" :key="item.sector" class="ppp-sector-item">
                      <div class="sector-name">{{ item.sector }}</div>
                      <div class="suitability-bars">
                        <div class="suitability-bar high" :style="{ width: (item.high / (item.high + item.medium + item.low) * 100) + '%' }">
                          <span v-if="item.high > 0" class="suitability-count">{{ item.high }}</span>
                        </div>
                        <div class="suitability-bar medium" :style="{ width: (item.medium / (item.high + item.medium + item.low) * 100) + '%' }">
                          <span v-if="item.medium > 0" class="suitability-count">{{ item.medium }}</span>
                        </div>
                        <div class="suitability-bar low" :style="{ width: (item.low / (item.high + item.medium + item.low) * 100) + '%' }">
                          <span v-if="item.low > 0" class="suitability-count">{{ item.low }}</span>
                        </div>
                      </div>
                    </div>
                    <div class="suitability-legend">
                      <div class="legend-item">
                        <span class="legend-dot high"></span>
                        <span class="legend-text">High</span>
                      </div>
                      <div class="legend-item">
                        <span class="legend-dot medium"></span>
                        <span class="legend-text">Medium</span>
                      </div>
                      <div class="legend-item">
                        <span class="legend-dot low"></span>
                        <span class="legend-text">Low</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>

            <!-- Climate Change Risk Exposure Tab -->
            <div v-else-if="activeTab === 'climate-risk'" class="climate-risk-content">
              
              <!-- Section 1: Projects Exposed to Climate Change Vulnerabilities -->
              <div class="chart-card">
                <div class="chart-header">
                  <h3>PROJECTS EXPOSED TO CLIMATE CHANGE VULNERABILITIES</h3>
                </div>
                <div class="chart-content">
                  <div class="radar-chart">
                    <div class="radar-container">
                      <svg class="radar-svg" viewBox="0 0 300 300">
                        <!-- Radar grid -->
                        <g class="radar-grid">
                          <circle cx="150" cy="150" r="40" fill="none" stroke="#e0e0e0" stroke-width="1"/>
                          <circle cx="150" cy="150" r="80" fill="none" stroke="#e0e0e0" stroke-width="1"/>
                          <circle cx="150" cy="150" r="120" fill="none" stroke="#e0e0e0" stroke-width="1"/>
                          <!-- Radar lines -->
                          <line v-for="(hazard, index) in climateHazardData" :key="hazard.name" 
                                :x1="150" :y1="150" 
                                :x2="150 + 120 * Math.cos((index * 360 / climateHazardData.length - 90) * Math.PI / 180)" 
                                :y2="150 + 120 * Math.sin((index * 360 / climateHazardData.length - 90) * Math.PI / 180)" 
                                stroke="#e0e0e0" stroke-width="1"/>
                        </g>
                        <!-- Data points -->
                        <g class="radar-data">
                          <polygon :points="getRadarPoints()" 
                                   fill="rgba(0, 86, 179, 0.3)" 
                                   stroke="#0056b3" 
                                   stroke-width="2"/>
                          <circle v-for="(point, index) in getRadarPointsArray()" :key="index"
                                  :cx="point.x" :cy="point.y" r="4" 
                                  fill="#0056b3"/>
                        </g>
                        <!-- Labels -->
                        <g class="radar-labels">
                          <text v-for="(hazard, index) in climateHazardData" :key="hazard.name"
                                :x="150 + 135 * Math.cos((index * 360 / climateHazardData.length - 90) * Math.PI / 180)" 
                                :y="150 + 135 * Math.sin((index * 360 / climateHazardData.length - 90) * Math.PI / 180)" 
                                text-anchor="middle" 
                                dominant-baseline="middle" 
                                font-size="10" 
                                fill="#333">
                            {{ hazard.shortName }}
                          </text>
                        </g>
                      </svg>
                    </div>
                    <div class="radar-legend">
                      <div v-for="hazard in climateHazardData" :key="hazard.name" class="legend-item">
                        <span class="legend-text">{{ hazard.name }}: {{ hazard.count }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Section 2: Status of Project Climate Change Vulnerability Assessment -->
              <div class="chart-card">
                <div class="chart-header">
                  <h3>STATUS OF PROJECT CLIMATE CHANGE VULNERABILITY ASSESSMENT</h3>
                </div>
                <div class="chart-content">
                  <div class="horizontal-bar-chart">
                    <div v-for="item in vulnerabilityAssessmentData" :key="item.sector" class="horizontal-bar-item">
                      <div class="bar-labels">
                        <span class="bar-label">{{ item.sector }}</span>
                      </div>
                      <div class="bar-container">
                        <div class="bar-fill" :style="{ 
                          width: (item.count / maxVulnerabilityAssessment * 100) + '%',
                          backgroundColor: '#0056b3'
                        }"></div>
                      </div>
                      <div class="bar-value-container">
                        <span class="bar-value">{{ item.count }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Section 3: Exposure to Climate Change Risks -->
              <div class="chart-card risk-exposure-section">
                <div class="chart-header">
                  <h3>EXPOSURE TO CLIMATE CHANGE RISKS</h3>
                </div>
                <div class="chart-content">
                  <div class="risk-exposure-bars">
                    <div v-for="risk in riskExposureData" :key="risk.level" class="risk-bar-item">
                      <div class="risk-bar" :class="risk.level.toLowerCase().replace(' ', '-')">
                        <span class="risk-label">{{ risk.level }}</span>
                        <span class="risk-count">{{ risk.count }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Section 4: Risk Categories -->
              <div class="chart-card risk-categories-section">
                <div class="chart-header">
                  <h3>RISK CATEGORIES</h3>
                </div>
                <div class="chart-content">
                  <div class="risk-categories">
                    <div v-for="category in riskCategories" :key="category" class="risk-category-box">
                      {{ category }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Section 5: CCA Status -->
              <div class="chart-card cca-status-section">
                <div class="chart-header">
                  <h3>CCA STATUS</h3>
                </div>
                <div class="chart-content">
                  <div class="cca-status">
                    <div v-for="status in ccaStatusData" :key="status" class="cca-status-box">
                      {{ status }}
                    </div>
                  </div>
                </div>
              </div>

            </div>

            <!-- GHG Emissions Profile Tab -->
            <div v-else-if="activeTab === 'ghg-profile'" class="ghg-profile-content">
              
              <!-- Top Metrics -->
              <div class="ghg-metrics-section">
                <div class="ghg-metrics-grid">
                  <div v-for="metric in ghgMetrics" :key="metric.id" class="metric-card">
                    <div class="metric-content">
                      <div class="metric-value">{{ metric.value }}</div>
                      <div class="metric-label">{{ metric.label }}</div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Section 1: Sectorwise GHG Emissions Profile -->
              <div class="chart-card">
                <div class="chart-header">
                  <h3>SECTORWISE GHG EMISSIONS PROFILE (CO2e)</h3>
                </div>
                <div class="chart-content">
                  <div class="ghg-horizontal-chart">
                    <div class="ghg-chart-container">
                      <div v-for="item in ghgEmissionsData.filter(i => i.emission > 0 || i.reduction > 0)" :key="item.sector" class="ghg-sector-row">
                        <div class="ghg-sector-label">{{ item.sector }}</div>
                        <div class="ghg-bars-container">
                          <div class="ghg-bar-group">
                            <div class="ghg-emission-bar" :style="{ width: (item.emission / maxGHGEmission * 100) + '%' }">
                              <span class="ghg-bar-value">{{ item.emission.toLocaleString() }}</span>
                            </div>
                            <div class="ghg-reduction-bar" :style="{ width: (item.reduction / maxGHGEmission * 100) + '%' }">
                              <span class="ghg-bar-value">{{ item.reduction.toLocaleString() }}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="ghg-chart-legend">
                      <div class="legend-item">
                        <span class="legend-dot emission"></span>
                        <span class="legend-text">Annual GHG Emission</span>
                      </div>
                      <div class="legend-item">
                        <span class="legend-dot reduction"></span>
                        <span class="legend-text">Annual GHG Emission Reduction Planned</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>

            <!-- Viability Analysis Tab -->
            <div v-else-if="activeTab === 'viability-analysis'" class="viability-analysis-content">
              
              <!-- Sub-navigation for Viability Analysis -->
              <div class="viability-sub-nav">
                <div class="sub-nav-tabs">
                  <button 
                    v-for="tab in viabilityTabs" 
                    :key="tab.id"
                    :class="['sub-nav-tab', { 'active': viabilityActiveTab === tab.id }]"
                    @click="setViabilityTab(tab.id)"
                  >
                    <span class="tab-icon">{{ tab.icon }}</span>
                    <span class="tab-text">{{ tab.name }}</span>
                  </button>
                </div>
              </div>
              
              <!-- Tab Content -->
              <div class="viability-tab-content">
                
                <!-- Ease of Implementation Tab -->
                <div v-if="viabilityActiveTab === 'ease-implementation'" class="ease-implementation-tab">
                  <div class="chart-card">
                    <div class="chart-header">
                      <h3>EASE OF IMPLEMENTATION</h3>
                    </div>
                    <div class="chart-content">
                      <div class="viability-legend">
                        <div class="legend-item">
                          <span class="legend-dot high"></span>
                          <span class="legend-text">High</span>
                        </div>
                        <div class="legend-item">
                          <span class="legend-dot med-high"></span>
                          <span class="legend-text">Med-High</span>
                        </div>
                        <div class="legend-item">
                          <span class="legend-dot medium"></span>
                          <span class="legend-text">Medium</span>
                        </div>
                        <div class="legend-item">
                          <span class="legend-dot med-low"></span>
                          <span class="legend-text">Med-Low</span>
                        </div>
                        <div class="legend-item">
                          <span class="legend-dot low"></span>
                          <span class="legend-text">Low</span>
                        </div>
                      </div>
                      <div class="viability-chart">
                        <div v-for="item in easeImplementationData.filter(i => i.high + i.medHigh + i.medium + i.medLow + i.low > 0)" :key="item.sector" class="viability-sector-row">
                          <div class="viability-sector-label">{{ item.sector }}</div>
                          <div class="viability-bars">
                            <div class="viability-bar high" v-if="item.high > 0" :style="{ width: getViabilityBarWidth(item, 'high') + '%' }">
                              <span class="viability-count">{{ item.high }}</span>
                            </div>
                            <div class="viability-bar med-high" v-if="item.medHigh > 0" :style="{ width: getViabilityBarWidth(item, 'medHigh') + '%' }">
                              <span class="viability-count">{{ item.medHigh }}</span>
                            </div>
                            <div class="viability-bar medium" v-if="item.medium > 0" :style="{ width: getViabilityBarWidth(item, 'medium') + '%' }">
                              <span class="viability-count">{{ item.medium }}</span>
                            </div>
                            <div class="viability-bar med-low" v-if="item.medLow > 0" :style="{ width: getViabilityBarWidth(item, 'medLow') + '%' }">
                              <span class="viability-count">{{ item.medLow }}</span>
                            </div>
                            <div class="viability-bar low" v-if="item.low > 0" :style="{ width: getViabilityBarWidth(item, 'low') + '%' }">
                              <span class="viability-count">{{ item.low }}</span>
                            </div>
                          </div>
                          <div class="viability-total">Total: {{ item.high + item.medHigh + item.medium + item.medLow + item.low }}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <!-- E&S Sustainability Tab -->
                <div v-else-if="viabilityActiveTab === 'es-sustainability'" class="es-sustainability-tab">
                  <div class="three-charts-grid">
                    
                    <!-- E&S Sustainability Chart -->
                    <div class="chart-card">
                      <div class="chart-header">
                        <h3>E&S SUSTAINABILITY</h3>
                      </div>
                      <div class="chart-content">
                        <div class="simple-viability-legend">
                          <div class="legend-item">
                            <span class="legend-dot high"></span>
                            <span class="legend-text">High</span>
                          </div>
                          <div class="legend-item">
                            <span class="legend-dot medium"></span>
                            <span class="legend-text">Medium</span>
                          </div>
                          <div class="legend-item">
                            <span class="legend-dot low"></span>
                            <span class="legend-text">Low</span>
                          </div>
                        </div>
                        <div class="simple-viability-chart">
                          <div v-for="item in esSustainabilityData.filter(i => i.high + i.medium + i.low > 0)" :key="item.sector" class="simple-viability-row">
                            <div class="simple-sector-label">{{ item.sector }}</div>
                            <div class="simple-bars">
                              <div class="simple-bar high" v-if="item.high > 0">
                                <span class="simple-count">{{ item.high }}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <!-- Commercial Potential Chart -->
                    <div class="chart-card">
                      <div class="chart-header">
                        <h3>COMMERCIAL POTENTIAL</h3>
                      </div>
                      <div class="chart-content">
                        <div class="simple-viability-legend">
                          <div class="legend-item">
                            <span class="legend-dot high"></span>
                            <span class="legend-text">High</span>
                          </div>
                          <div class="legend-item">
                            <span class="legend-dot medium"></span>
                            <span class="legend-text">Medium</span>
                          </div>
                          <div class="legend-item">
                            <span class="legend-dot low"></span>
                            <span class="legend-text">Low</span>
                          </div>
                        </div>
                        <div class="simple-viability-chart">
                          <div v-for="item in commercialPotentialData.filter(i => i.high + i.medium + i.low > 0)" :key="item.sector" class="simple-viability-row">
                            <div class="simple-sector-label">{{ item.sector }}</div>
                            <div class="simple-bars">
                              <div class="simple-bar high" v-if="item.high > 0">
                                <span class="simple-count">{{ item.high }}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <!-- Fiscal Affordability Chart -->
                    <div class="chart-card">
                      <div class="chart-header">
                        <h3>FISCAL AFFORDABILITY</h3>
                      </div>
                      <div class="chart-content">
                        <div class="simple-viability-legend">
                          <div class="legend-item">
                            <span class="legend-dot high"></span>
                            <span class="legend-text">High</span>
                          </div>
                          <div class="legend-item">
                            <span class="legend-dot medium"></span>
                            <span class="legend-text">Medium</span>
                          </div>
                          <div class="legend-item">
                            <span class="legend-dot low"></span>
                            <span class="legend-text">Low</span>
                          </div>
                        </div>
                        <div class="simple-viability-chart">
                          <div v-for="item in fiscalAffordabilityData.filter(i => i.high + i.medium + i.low > 0)" :key="item.sector" class="simple-viability-row">
                            <div class="simple-sector-label">{{ item.sector }}</div>
                            <div class="simple-bars">
                              <div class="simple-bar high" v-if="item.high > 0">
                                <span class="simple-count">{{ item.high }}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                  </div>
                </div>
                
                <!-- Climate Change Resilience Profile Tab -->
                <div v-else-if="viabilityActiveTab === 'climate-resilience'" class="viability-resilience-tab">
                  <div class="chart-card">
                    <div class="chart-header">
                      <h3>CLIMATE CHANGE RESILIENCE PROFILE</h3>
                    </div>
                    <div class="chart-content">
                      <div class="simple-viability-legend">
                        <div class="legend-item">
                          <span class="legend-dot high"></span>
                          <span class="legend-text">High</span>
                        </div>
                        <div class="legend-item">
                          <span class="legend-dot medium"></span>
                          <span class="legend-text">Medium</span>
                        </div>
                        <div class="legend-item">
                          <span class="legend-dot low"></span>
                          <span class="legend-text">Low</span>
                        </div>
                      </div>
                      <div class="simple-viability-chart">
                        <div v-for="item in viabilityResilienceData.filter(i => i.high + i.medium + i.low > 0)" :key="item.sector" class="simple-viability-row">
                          <div class="simple-sector-label">{{ item.sector }}</div>
                          <div class="simple-bars">
                            <div class="simple-bar high" v-if="item.high > 0">
                              <span class="simple-count">{{ item.high }}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <!-- Help Section Tab -->
                <div v-else-if="viabilityActiveTab === 'help'" class="help-tab">
                  <div class="chart-card">
                    <div class="chart-header">
                      <h3>HELP SECTION</h3>
                    </div>
                    <div class="chart-content">
                      <div class="help-placeholder">
                        <div class="help-content">
                          <h4>Help & Documentation</h4>
                          <p>This section will contain helpful information and documentation for using the Viability Analysis features.</p>
                          <div class="help-icon">📚</div>
                          <p><em>Content to be determined later</em></p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
              </div>
              
            </div>

            <!-- Other Tab Contents (Placeholder) -->
            <div v-else class="tab-content">
              <div class="tab-placeholder">
                <div class="placeholder-content">
                  <h2>{{ getActiveTabName() }}</h2>
                  <p>This section is under development and will include comprehensive {{ getActiveTabName().toLowerCase() }} analytics.</p>
                  <div class="placeholder-icon">📊</div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
export default {
  name: 'PipelineAnalytics',
  data() {
    return {
      activeTab: 'dashboard',
      contentLoaded: true,
      navigationTabs: [
        {
          id: 'dashboard',
          name: 'Dashboard',
          icon: '📊'
        },
        {
          id: 'climate-risk',
          name: 'Climate Change Risk Exposure',
          icon: '🌡️'
        },
        {
          id: 'climate-resilience',
          name: 'Climate Change Resilience',
          icon: '🛡️'
        },
        {
          id: 'ghg-profile',
          name: 'GHG Profile',
          icon: '🌱'
        },
        {
          id: 'viability-analysis',
          name: 'Viability Analysis',
          icon: '📈'
        },
        {
          id: 'customized-analysis',
          name: 'Customized Analysis',
          icon: '⚙️'
        },
        {
          id: 'ranking',
          name: 'Ranking of Projects',
          icon: '🏆'
        },
        {
          id: 'download-data',
          name: 'Download data',
          icon: '💾'
        },
        {
          id: 'back-main',
          name: 'Back to main menu',
          icon: '←'
        }
      ],
      // Climate Change Resilience Data
      resilienceScoreData: [
        { label: 'Materially Deficient', count: 0, color: '#d32f2f' },
        { label: 'Deficient', count: 0, color: '#f57c00' },
        { label: 'Very Weak', count: 0, color: '#fbc02d' },
        { label: 'Weak', count: 1, color: '#689f38' },
        { label: 'Moderately Weak', count: 0, color: '#388e3c' },
        { label: 'Moderately Adequate', count: 0, color: '#1976d2' },
        { label: 'Adequate', count: 4, color: '#0056b3' },
        { label: 'Strong', count: 29, color: '#004494' },
        { label: 'Very Strong', count: 0, color: '#002171' }
      ],
      sectorResilienceData: [
        { sector: 'Energy', score: 4 },
        { sector: 'Agriculture', score: 9 },
        { sector: 'Education', score: 4 },
        { sector: 'Sports, culture and recreation', score: 4 },
        { sector: 'Urban infrastructure', score: 4 },
        { sector: 'Solid waste', score: 4 },
        { sector: 'Water', score: 4 },
        { sector: 'Housing and accommodation', score: 1 },
        { sector: 'Health', score: 1 },
        { sector: 'Other', score: 1 },
        { sector: 'Tourism', score: 1 },
        { sector: 'Transport', score: 1 },
        { sector: 'Telecom/ICT', score: 1 }
      ],
      pppSuitabilityData: [
        { sector: 'Agriculture', high: 1, medium: 2, low: 3 },
        { sector: 'Energy', high: 1, medium: 2, low: 3 },
        { sector: 'Education', high: 2, medium: 2, low: 3 },
        { sector: 'Sports, culture and recreation', high: 1, medium: 2, low: 3 },
        { sector: 'Solid waste', high: 2, medium: 1, low: 1 },
        { sector: 'Urban infrastructure', high: 1, medium: 1, low: 1 },
        { sector: 'Water', high: 1, medium: 1, low: 1 },
        { sector: 'Health', high: 1, medium: 1, low: 1 },
        { sector: 'Housing and accommodation', high: 1, medium: 1, low: 1 },
        { sector: 'Other', high: 0, medium: 0, low: 0 },
        { sector: 'Transport', high: 0, medium: 0, low: 0 },
        { sector: 'Tourism', high: 0, medium: 0, low: 0 },
        { sector: 'Telecom/ICT', high: 0, medium: 0, low: 0 }
      ],
      // Climate Change Risk Exposure Data
      climateHazardData: [
        { name: 'Floods/Typhoon/Hurricanes', shortName: 'Floods', count: 12 },
        { name: 'Earthquake', shortName: 'Earthquake', count: 19 },
        { name: 'Extreme Heat', shortName: 'Heat', count: 16 },
        { name: 'Extreme Snowfall/Landslides', shortName: 'Snowfall', count: 18 },
        { name: 'Droughts', shortName: 'Droughts', count: 13 },
        { name: 'Volcano', shortName: 'Volcano', count: 2 },
        { name: 'Water scarcity', shortName: 'Water', count: 5 },
        { name: 'Urban Flood', shortName: 'Urban Flood', count: 3 },
        { name: 'Other', shortName: 'Other', count: 5 }
      ],
      vulnerabilityAssessmentData: [
        { sector: 'Energy', count: 5 },
        { sector: 'Urban infrastructure', count: 3 },
        { sector: 'Transport', count: 1 },
        { sector: 'Water', count: 2 },
        { sector: 'Agriculture', count: 4 },
        { sector: 'Health', count: 2 }
      ],
      riskExposureData: [
        { level: 'Low', count: 1 },
        { level: 'Medium', count: 5 },
        { level: 'High', count: 2 },
        { level: 'Very High', count: 1 }
      ],
      riskCategories: [
        'Design & Construction Risks',
        'O & M Risks',
        'Financial Risks',
        'Legal Risks'
      ],
      ccaStatusData: [
        'CCA Done',
        'CCA Partially Done',
        'No CCA'
      ],
      // Viability Analysis Data
      viabilityActiveTab: 'ease-implementation',
      viabilityTabs: [
        { id: 'ease-implementation', name: 'Ease of Implementation', icon: '⚡' },
        { id: 'es-sustainability', name: 'E&S Sustainability, Commercial Potential, Fiscal Affordability', icon: '📊' },
        { id: 'climate-resilience', name: 'Climate Change Resilience Profile', icon: '🛡️' },
        { id: 'help', name: 'Help Section', icon: '❓' }
      ],
      easeImplementationData: [
        { sector: 'Agriculture', high: 2, medHigh: 0, medium: 4, medLow: 0, low: 0 },
        { sector: 'Sports, culture and recreation', high: 4, medHigh: 0, medium: 0, medLow: 0, low: 0 },
        { sector: 'Energy', high: 1, medHigh: 2, medium: 1, medLow: 0, low: 0 },
        { sector: 'Education', high: 2, medHigh: 1, medium: 1, medLow: 0, low: 0 },
        { sector: 'Urban infrastructure', high: 1, medHigh: 1, medium: 1, medLow: 0, low: 0 },
        { sector: 'Solid waste', high: 2, medHigh: 1, medium: 1, medLow: 0, low: 0 },
        { sector: 'Water', high: 1, medHigh: 2, medium: 1, medLow: 0, low: 0 },
        { sector: 'Health', high: 0, medHigh: 1, medium: 0, medLow: 0, low: 0 },
        { sector: 'Housing and accommodation', high: 0, medHigh: 1, medium: 0, medLow: 0, low: 0 },
        { sector: 'Transport', high: 0, medHigh: 1, medium: 0, medLow: 0, low: 0 },
        { sector: 'Other', high: 0, medHigh: 1, medium: 0, medLow: 0, low: 0 }
      ],
      esSustainabilityData: [
        { sector: 'Agriculture', high: 6, medium: 0, low: 0 },
        { sector: 'Sports, culture and recreation', high: 4, medium: 0, low: 0 },
        { sector: 'Energy', high: 4, medium: 0, low: 0 },
        { sector: 'Education', high: 4, medium: 0, low: 0 },
        { sector: 'Urban infrastructure', high: 3, medium: 0, low: 0 },
        { sector: 'Solid waste', high: 4, medium: 0, low: 0 },
        { sector: 'Water', high: 4, medium: 0, low: 0 },
        { sector: 'Health', high: 1, medium: 0, low: 0 },
        { sector: 'Housing and accommodation', high: 1, medium: 0, low: 0 },
        { sector: 'Transport', high: 1, medium: 0, low: 0 },
        { sector: 'Other', high: 1, medium: 0, low: 0 }
      ],
      commercialPotentialData: [
        { sector: 'Agriculture', high: 6, medium: 0, low: 0 },
        { sector: 'Sports, culture and recreation', high: 4, medium: 0, low: 0 },
        { sector: 'Energy', high: 4, medium: 0, low: 0 },
        { sector: 'Education', high: 4, medium: 0, low: 0 },
        { sector: 'Urban infrastructure', high: 3, medium: 0, low: 0 },
        { sector: 'Solid waste', high: 4, medium: 0, low: 0 },
        { sector: 'Water', high: 4, medium: 0, low: 0 },
        { sector: 'Health', high: 1, medium: 0, low: 0 },
        { sector: 'Housing and accommodation', high: 1, medium: 0, low: 0 },
        { sector: 'Transport', high: 1, medium: 0, low: 0 },
        { sector: 'Other', high: 1, medium: 0, low: 0 }
      ],
      fiscalAffordabilityData: [
        { sector: 'Agriculture', high: 6, medium: 0, low: 0 },
        { sector: 'Sports, culture and recreation', high: 4, medium: 0, low: 0 },
        { sector: 'Energy', high: 4, medium: 0, low: 0 },
        { sector: 'Education', high: 4, medium: 0, low: 0 },
        { sector: 'Urban infrastructure', high: 3, medium: 0, low: 0 },
        { sector: 'Solid waste', high: 4, medium: 0, low: 0 },
        { sector: 'Water', high: 4, medium: 0, low: 0 },
        { sector: 'Health', high: 1, medium: 0, low: 0 },
        { sector: 'Housing and accommodation', high: 1, medium: 0, low: 0 },
        { sector: 'Transport', high: 1, medium: 0, low: 0 },
        { sector: 'Other', high: 1, medium: 0, low: 0 }
      ],
      viabilityResilienceData: [
        { sector: 'Agriculture', high: 6, medium: 0, low: 0 },
        { sector: 'Sports, culture and recreation', high: 4, medium: 0, low: 0 },
        { sector: 'Energy', high: 4, medium: 0, low: 0 },
        { sector: 'Education', high: 4, medium: 0, low: 0 },
        { sector: 'Urban infrastructure', high: 3, medium: 0, low: 0 },
        { sector: 'Solid waste', high: 4, medium: 0, low: 0 },
        { sector: 'Water', high: 4, medium: 0, low: 0 },
        { sector: 'Health', high: 1, medium: 0, low: 0 },
        { sector: 'Housing and accommodation', high: 1, medium: 0, low: 0 },
        { sector: 'Transport', high: 1, medium: 0, low: 0 },
        { sector: 'Other', high: 1, medium: 0, low: 0 }
      ],
      // GHG Emissions Data
      ghgEmissionsData: [
        { sector: 'Energy', emission: 45750, reduction: 18500 },
        { sector: 'Solid waste', emission: 33836, reduction: 11722 },
        { sector: 'Education', emission: 32508, reduction: 12166 },
        { sector: 'Agriculture', emission: 22598, reduction: 4780 },
        { sector: 'Sports, culture and recreation', emission: 15250, reduction: 4200 },
        { sector: 'Other', emission: 11299, reduction: 1990 },
        { sector: 'Housing and accommodation', emission: 11299, reduction: 1990 },
        { sector: 'Telecom/ICT', emission: 0, reduction: 0 },
        { sector: 'Transport', emission: 0, reduction: 0 },
        { sector: 'Tourism', emission: 0, reduction: 0 },
        { sector: 'Health', emission: 0, reduction: 0 },
        { sector: 'Water', emission: 0, reduction: 0 },
        { sector: 'Urban infrastructure', emission: 0, reduction: 0 }
      ],
      // Sample project data derived from available projects
      projectsData: [
        {
          id: 1,
          name: 'Metro Line Extension Project',
          sector: 'Transport',
          stage: 'Implementation',
          totalCost: 450,
          implementingAgency: 'Department of Transportation',
          timeline: 48,
          fundingSource: 'PPP',
          governmentSupport: true,
          unsolicited: false,
          psatScore: 78,
          type: 'Infrastructure'
        },
        {
          id: 2,
          name: 'Water Treatment Facility',
          sector: 'Water',
          stage: 'Feasibility',
          totalCost: 280,
          implementingAgency: 'Ministry of Water Resources',
          timeline: 36,
          fundingSource: 'Government Budget',
          governmentSupport: false,
          unsolicited: true,
          psatScore: 85,
          type: 'Infrastructure'
        },
        {
          id: 3,
          name: 'Solar Power Plant',
          sector: 'Energy',
          stage: 'Concept',
          totalCost: 320,
          implementingAgency: 'Ministry of Energy',
          timeline: 42,
          fundingSource: 'International Loan',
          governmentSupport: true,
          unsolicited: false,
          psatScore: 72,
          type: 'Infrastructure'
        },
        {
          id: 4,
          name: 'Hospital Infrastructure',
          sector: 'Health',
          stage: 'Pre-feasibility',
          totalCost: 180,
          implementingAgency: 'Ministry of Health',
          timeline: 30,
          fundingSource: 'Grant',
          governmentSupport: true,
          unsolicited: false,
          psatScore: 68,
          type: 'Social Infrastructure'
        },
        {
          id: 5,
          name: 'Smart City Initiative',
          sector: 'Urban infrastructure',
          stage: 'Procurement',
          totalCost: 520,
          implementingAgency: 'Ministry of Urban Development',
          timeline: 60,
          fundingSource: 'PPP',
          governmentSupport: false,
          unsolicited: true,
          psatScore: 81,
          type: 'Digital Infrastructure'
        }
      ]
    }
  },
  computed: {
    dashboardMetrics() {
      return [
        {
          id: 'projects',
          icon: '📋',
          value: this.projectsData.length,
          label: 'Number of projects'
        },
        {
          id: 'pipeline-size',
          icon: '💰',
          value: `$${this.totalPipelineSize}M`,
          label: 'Total size of the pipeline'
        },
        {
          id: 'agencies',
          icon: '🏛️',
          value: this.uniqueAgencies.length,
          label: 'Number of implementing government agencies'
        },
        {
          id: 'government-support',
          icon: '🤝',
          value: this.projectsNeedingSupport,
          label: 'Number of projects that need government support'
        },
        {
          id: 'sectors',
          icon: '🏗️',
          value: this.uniqueSectors.length,
          label: 'Number of sectors'
        },
        {
          id: 'unsolicited',
          icon: '📨',
          value: this.unsolicitedProposals,
          label: 'Number of unsolicited proposals'
        }
      ]
    },
    totalPipelineSize() {
      return this.projectsData.reduce((sum, project) => sum + project.totalCost, 0)
    },
    uniqueAgencies() {
      return [...new Set(this.projectsData.map(p => p.implementingAgency))]
    },
    uniqueSectors() {
      return [...new Set(this.projectsData.map(p => p.sector))]
    },
    projectsNeedingSupport() {
      return this.projectsData.filter(p => p.governmentSupport).length
    },
    unsolicitedProposals() {
      return this.projectsData.filter(p => p.unsolicited).length
    },
    totalProjects() {
      return this.projectsData.length
    },
    portfolioStageData() {
      const stageGroups = this.projectsData.reduce((acc, project) => {
        acc[project.stage] = (acc[project.stage] || 0) + 1
        return acc
      }, {})
      
      const colors = ['#0056b3', '#1e73e8', '#4285f4', '#6fa8f5', '#9cc2f7']
      let colorIndex = 0
      
      return Object.entries(stageGroups).map(([stage, count]) => ({
        stage,
        count,
        percentage: (count / this.totalProjects * 100).toFixed(1),
        color: colors[colorIndex++ % colors.length]
      }))
    },
    paymentSourceData() {
      const sourceGroups = this.projectsData.reduce((acc, project) => {
        acc[project.fundingSource] = (acc[project.fundingSource] || 0) + 1
        return acc
      }, {})
      
      return Object.entries(sourceGroups).map(([source, count]) => ({
        source,
        count
      })).sort((a, b) => b.count - a.count)
    },
    maxPaymentSource() {
      return Math.max(...this.paymentSourceData.map(item => item.count))
    },
    projectTypeData() {
      const typeGroups = this.projectsData.reduce((acc, project) => {
        acc[project.type] = (acc[project.type] || 0) + 1
        return acc
      }, {})
      
      return Object.entries(typeGroups).map(([type, count]) => ({
        type,
        count
      })).sort((a, b) => b.count - a.count)
    },
    maxProjectType() {
      return Math.max(...this.projectTypeData.map(item => item.count))
    },
    averagePSATScore() {
      const totalScore = this.projectsData.reduce((sum, project) => sum + project.psatScore, 0)
      return totalScore / this.projectsData.length
    },
    sectorData() {
      const sectorGroups = this.projectsData.reduce((acc, project) => {
        acc[project.sector] = (acc[project.sector] || 0) + 1
        return acc
      }, {})
      
      return Object.entries(sectorGroups).map(([sector, count]) => ({
        sector: sector.length > 8 ? sector.substring(0, 8) + '...' : sector,
        count
      })).sort((a, b) => b.count - a.count)
    },
    maxSectorCount() {
      return Math.max(...this.sectorData.map(item => item.count))
    },
    investmentSizeData() {
      const ranges = {
        '0-100M': 0,
        '100-300M': 0,
        '300-500M': 0,
        '500M+': 0
      }
      
      this.projectsData.forEach(project => {
        const cost = project.totalCost
        if (cost <= 100) ranges['0-100M']++
        else if (cost <= 300) ranges['100-300M']++
        else if (cost <= 500) ranges['300-500M']++
        else ranges['500M+']++
      })
      
      return Object.entries(ranges).map(([range, count]) => ({
        range,
        count
      }))
    },
    maxInvestmentCount() {
      return Math.max(...this.investmentSizeData.map(item => item.count))
    },
    // Climate Change Resilience Computed Properties
    totalResilienceProjects() {
      return this.resilienceScoreData.reduce((sum, item) => sum + item.count, 0)
    },
    maxSectorResilienceScore() {
      return Math.max(...this.sectorResilienceData.map(item => item.score))
    },
    // Climate Risk Computed Properties
    maxVulnerabilityAssessment() {
      return Math.max(...this.vulnerabilityAssessmentData.map(item => item.count))
    },
    maxClimateHazardCount() {
      return Math.max(...this.climateHazardData.map(item => item.count))
    },
    // GHG Emissions Computed Properties
    ghgMetrics() {
      const projectsWithGHG = this.ghgEmissionsData.filter(item => item.emission > 0).length
      const totalEmission = this.ghgEmissionsData.reduce((sum, item) => sum + item.emission, 0)
      const totalReduction = this.ghgEmissionsData.reduce((sum, item) => sum + item.reduction, 0)
      const reductionPercentage = totalEmission > 0 ? Math.round((totalReduction / totalEmission) * 100) : 0
      
      return [
        {
          id: 'ghg-projects',
          value: `${projectsWithGHG} (${Math.round((projectsWithGHG / this.ghgEmissionsData.length) * 100)}%)`,
          label: 'Project with GHG Data'
        },
        {
          id: 'annual-emission',
          value: `${(totalEmission / 1000000).toFixed(2)} MnCO2e`,
          label: 'Annual GHG Emission'
        },
        {
          id: 'reduction-planned',
          value: `${reductionPercentage}%`,
          label: 'Annual GHG Emission Reduction Planned'
        }
      ]
    },
    maxGHGEmission() {
      return Math.max(...this.ghgEmissionsData.map(item => Math.max(item.emission, item.reduction)))
    }
  },
  methods: {
    // Climate Resilience Methods
    getResilienceSegmentAngle(count) {
      const total = this.totalResilienceProjects
      return total > 0 ? (count / total) * 360 : 0
    },
    // Climate Risk Methods
    getRadarPoints() {
      const points = this.climateHazardData.map((hazard, index) => {
        const angle = (index * 360 / this.climateHazardData.length - 90) * Math.PI / 180
        const radius = (hazard.count / this.maxClimateHazardCount) * 120
        const x = 150 + radius * Math.cos(angle)
        const y = 150 + radius * Math.sin(angle)
        return `${x},${y}`
      })
      return points.join(' ')
    },
    getRadarPointsArray() {
      return this.climateHazardData.map((hazard, index) => {
        const angle = (index * 360 / this.climateHazardData.length - 90) * Math.PI / 180
        const radius = (hazard.count / this.maxClimateHazardCount) * 120
        const x = 150 + radius * Math.cos(angle)
        const y = 150 + radius * Math.sin(angle)
        return { x, y }
      })
    },
    // Viability Analysis Methods
    setViabilityTab(tabId) {
      this.viabilityActiveTab = tabId
    },
    getViabilityBarWidth(item, type) {
      const total = item.high + item.medHigh + item.medium + item.medLow + item.low
      if (total === 0) return 0
      const value = item[type] || 0
      return (value / total) * 100
    },
    setActiveTab(tabId) {
      if (tabId === 'back-main') {
        this.goBack()
        return
      }
      
      this.contentLoaded = false
      setTimeout(() => {
        this.activeTab = tabId
        this.contentLoaded = true
      }, 150)
    },
    getActiveTabName() {
      const tab = this.navigationTabs.find(t => t.id === this.activeTab)
      return tab ? tab.name : 'Dashboard'
    },
    getSegmentOffset(index) {
      let offset = 0
      for (let i = 0; i < index; i++) {
        offset += parseFloat(this.portfolioStageData[i].percentage)
      }
      return offset
    },
    goBack() {
      this.$emit('navigate', 'dashboard')
    },
    navigateToHome() {
      this.$emit('navigate', 'landing')
    }
  }
}
</script>

<style scoped>
.pipeline-analytics {
  min-height: 100vh;
  background: rgb(229, 229, 229);
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

.dashboard-header {
  background: white;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  padding: 1rem 0;
  position: sticky;
  top: 0;
  z-index: 100;
}

.header-content {
  max-width: 1600px;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.logo-section {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.kpmg-logo {
  background: white;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  transition: transform 0.3s ease;
  display: flex;
  align-items: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.kpmg-logo:hover {
  transform: scale(1.05);
}

.kpmg-logo-img {
  height: 32px;
  width: auto;
}

.logo-section h1 {
  font-size: 1.8rem;
  color: #0056b3;
  margin: 0;
  font-weight: 600;
}

.back-btn {
  background: transparent;
  color: #0056b3;
  border: 1px solid #0056b3;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s;
}

.back-btn:hover {
  background: #0056b3;
  color: white;
}

.main-content {
  padding: 0;
}

/* Analytics Layout */
.analytics-layout {
  display: flex;
  min-height: calc(100vh - 80px);
}

/* Left Navigation */
.left-navigation {
  width: 280px;
  background: #0056b3;
  padding: 0;
  flex-shrink: 0;
}

.nav-tabs {
  list-style: none;
  padding: 0;
  margin: 0;
}

.nav-tab {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  cursor: pointer;
  transition: all 0.3s;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.8);
}

.nav-tab:hover {
  background: rgba(255, 255, 255, 0.1);
  color: white;
}

.nav-tab.active {
  background: rgba(255, 255, 255, 0.15);
  color: white;
  border-right: 4px solid white;
}

.tab-icon {
  font-size: 1.2rem;
  width: 24px;
  text-align: center;
}

.tab-text {
  font-weight: 500;
  font-size: 0.9rem;
  line-height: 1.2;
}

/* Content Area */
.content-area {
  flex: 1;
  padding: 2rem;
  overflow-y: auto;
}

.content-container {
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.3s ease;
}

.content-container.fade-in {
  opacity: 1;
  transform: translateY(0);
}

/* Dashboard Content */
.dashboard-content {
  display: flex;
  gap: 2rem;
  max-width: 1400px;
}

/* Metrics Section */
.metrics-section {
  width: 320px;
  flex-shrink: 0;
}

.metrics-grid {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.metric-card {
  background: #0056b3;
  color: white;
  padding: 1.5rem;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 1rem;
  box-shadow: 0 4px 15px rgba(0, 86, 179, 0.2);
  transition: transform 0.3s, box-shadow 0.3s;
}

.metric-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 86, 179, 0.3);
}

.metric-icon {
  font-size: 2rem;
  opacity: 0.9;
}

.metric-content {
  flex: 1;
}

.metric-value {
  font-size: 1.8rem;
  font-weight: 700;
  line-height: 1;
  margin-bottom: 0.25rem;
}

.metric-label {
  font-size: 0.9rem;
  opacity: 0.9;
  line-height: 1.2;
}

/* Charts Section */
.charts-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.charts-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
}

.chart-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.08);
  transition: transform 0.3s, box-shadow 0.3s;
}

.chart-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0,0,0,0.12);
}

.chart-header {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #f0f0f0;
  background: #0056b3;
  color: white;
  border-radius: 12px 12px 0 0;
}

.chart-header h3 {
  font-size: 0.9rem;
  font-weight: 600;
  margin: 0;
  letter-spacing: 0.5px;
}

.chart-content {
  padding: 1.5rem;
}

/* Pie Chart */
.pie-chart {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.pie-center {
  position: relative;
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: conic-gradient(
    #0056b3 0deg 72deg,
    #1e73e8 72deg 144deg,
    #4285f4 144deg 216deg,
    #6fa8f5 216deg 288deg,
    #9cc2f7 288deg 360deg
  );
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
}

.pie-center::before {
  content: '';
  position: absolute;
  width: 80px;
  height: 80px;
  background: white;
  border-radius: 50%;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.pie-total {
  font-size: 1.5rem;
  font-weight: 700;
  color: #0056b3;
  z-index: 1;
  line-height: 1;
}

.pie-label {
  font-size: 0.8rem;
  color: #666;
  z-index: 1;
}

.pie-legend {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

.legend-text {
  font-size: 0.8rem;
  color: #333;
}

/* Bar Charts */
.bar-chart {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.bar-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.bar-container {
  flex: 1;
  height: 24px;
  background: #f0f0f0;
  border-radius: 4px;
  overflow: hidden;
}

.bar-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.6s ease;
}

.bar-labels {
  min-width: 120px;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.bar-label {
  font-size: 0.8rem;
  color: #333;
  font-weight: 500;
}

.bar-value {
  font-size: 0.7rem;
  color: #666;
  font-weight: 600;
}

/* Gauge Chart */
.gauge-chart {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.gauge-container {
  position: relative;
  width: 140px;
  height: 140px;
}

.gauge-arc {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.gauge-arc::before {
  content: '';
  position: absolute;
  width: 100px;
  height: 100px;
  background: white;
  border-radius: 50%;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.gauge-center {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1;
}

.gauge-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #0056b3;
  line-height: 1;
}

.gauge-label {
  font-size: 0.7rem;
  color: #666;
  text-align: center;
}

.gauge-legend {
  width: 100%;
}

.gauge-range {
  display: flex;
  justify-content: space-between;
  font-size: 0.7rem;
  color: #666;
  margin-top: 0.5rem;
}

/* Vertical Bar Charts */
.vertical-bar-chart {
  display: flex;
  justify-content: space-around;
  align-items: end;
  height: 120px;
  gap: 0.5rem;
}

.vertical-bar-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
}

.vertical-bar-container {
  width: 100%;
  max-width: 40px;
  height: 80px;
  background: #f0f0f0;
  border-radius: 4px;
  display: flex;
  align-items: end;
  margin-bottom: 0.5rem;
}

.vertical-bar-fill {
  width: 100%;
  border-radius: 4px;
  transition: height 0.6s ease;
  min-height: 2px;
}

.vertical-bar-label {
  font-size: 0.7rem;
  color: #333;
  text-align: center;
  margin-bottom: 0.25rem;
  font-weight: 500;
}

.vertical-bar-value {
  font-size: 0.8rem;
  color: #0056b3;
  font-weight: 600;
}

/* Tab Placeholder */
.tab-content {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
}

.tab-placeholder {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.08);
  padding: 3rem;
  text-align: center;
  max-width: 500px;
  width: 100%;
}

.placeholder-content h2 {
  color: #0056b3;
  font-size: 1.8rem;
  margin-bottom: 1rem;
  font-weight: 600;
}

.placeholder-content p {
  color: #666;
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 2rem;
}

.placeholder-icon {
  font-size: 4rem;
  opacity: 0.3;
}

/* Responsive Design */
@media (max-width: 1400px) {
  .dashboard-content {
    flex-direction: column;
  }
  
  .metrics-section {
    width: 100%;
  }
  
  .metrics-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1rem;
  }
}

@media (max-width: 1200px) {
  .charts-row {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .analytics-layout {
    flex-direction: column;
  }
  
  .left-navigation {
    width: 100%;
    order: 2;
  }
  
  .nav-tabs {
    display: flex;
    overflow-x: auto;
    white-space: nowrap;
  }
  
  .nav-tab {
    flex: 0 0 auto;
    min-width: 120px;
    padding: 0.75rem 1rem;
    border-bottom: none;
    border-right: 1px solid rgba(255, 255, 255, 0.1);
  }
  
  .nav-tab.active {
    border-right: 1px solid rgba(255, 255, 255, 0.1);
    border-bottom: 4px solid white;
  }
  
  .tab-text {
    font-size: 0.8rem;
  }
  
  .content-area {
    order: 1;
    padding: 1rem;
  }
  
  .charts-row {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .metrics-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 480px) {
  .header-content {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
  
  .metrics-grid {
    grid-template-columns: 1fr;
  }
  
  .nav-tab {
    min-width: 100px;
    padding: 0.5rem;
  }
  
  .tab-text {
    font-size: 0.7rem;
  }
  
  .metric-card {
    padding: 1rem;
  }
  
  .metric-value {
    font-size: 1.4rem;
  }
  
  .chart-content {
    padding: 1rem;
  }
}

/* Climate Change Resilience Styles */
.climate-resilience-content {
  display: flex;
  flex-direction: column;
  gap: 2rem;
  max-width: 1400px;
}

.resilience-gauge-container {
  position: relative;
  width: 200px;
  height: 200px;
  margin: 0 auto;
}

.resilience-gauge-arc {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: conic-gradient(
    #d32f2f 0deg 0deg,
    #f57c00 0deg 10deg,
    #fbc02d 10deg 20deg,
    #689f38 20deg 40deg,
    #388e3c 40deg 60deg,
    #1976d2 60deg 80deg,
    #0056b3 80deg 160deg,
    #004494 160deg 350deg,
    #002171 350deg 360deg
  );
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.resilience-gauge-arc::before {
  content: '';
  position: absolute;
  width: 140px;
  height: 140px;
  background: white;
  border-radius: 50%;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.resilience-legend {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
  margin-top: 1rem;
}

.horizontal-bar-chart {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.horizontal-bar-item {
  display: grid;
  grid-template-columns: 2fr 3fr 1fr;
  align-items: center;
  gap: 1rem;
}

.bar-value-container {
  text-align: right;
  font-weight: 600;
  color: #0056b3;
}

.ppp-suitability-chart {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.ppp-sector-item {
  display: grid;
  grid-template-columns: 2fr 3fr;
  align-items: center;
  gap: 1rem;
}

.sector-name {
  font-weight: 500;
  color: #333;
}

.suitability-bars {
  display: flex;
  height: 30px;
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid #e0e0e0;
}

.suitability-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 0.8rem;
  transition: all 0.3s;
}

.suitability-bar.high {
  background: #4caf50;
}

.suitability-bar.medium {
  background: #ff9800;
}

.suitability-bar.low {
  background: #f44336;
}

.suitability-count {
  color: white;
}

.suitability-legend {
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin-top: 1rem;
}

.legend-dot.high {
  background: #4caf50;
}

.legend-dot.medium {
  background: #ff9800;
}

.legend-dot.low {
  background: #f44336;
}

/* Climate Change Risk Exposure Styles */
.climate-risk-content {
  display: flex;
  flex-direction: column;
  gap: 2rem;
  max-width: 1400px;
}

.radar-chart {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
}

.radar-container {
  width: 300px;
  height: 300px;
}

.radar-svg {
  width: 100%;
  height: 100%;
}

.radar-legend {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
  max-width: 600px;
  text-align: center;
}

.risk-exposure-section .chart-content,
.risk-categories-section .chart-content,
.cca-status-section .chart-content {
  padding: 2rem;
}

.risk-exposure-bars {
  display: flex;
  gap: 1rem;
  justify-content: space-around;
}

.risk-bar-item {
  flex: 1;
}

.risk-bar {
  padding: 1rem;
  border-radius: 8px;
  text-align: center;
  color: white;
  font-weight: 600;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.risk-bar.low {
  background: #4caf50;
}

.risk-bar.medium {
  background: #ff9800;
}

.risk-bar.high {
  background: #f44336;
}

.risk-bar.very-high {
  background: #d32f2f;
}

.risk-label {
  font-size: 0.9rem;
}

.risk-count {
  font-size: 1.5rem;
  font-weight: 700;
}

.risk-categories {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
}

.risk-category-box {
  padding: 1.5rem;
  background: #0056b3;
  color: white;
  border-radius: 8px;
  text-align: center;
  font-weight: 600;
}

.cca-status {
  display: flex;
  gap: 1rem;
  justify-content: space-around;
}

.cca-status-box {
  flex: 1;
  padding: 1.5rem;
  background: #666;
  color: white;
  border-radius: 8px;
  text-align: center;
  font-weight: 600;
}

.cca-status-box:first-child {
  background: #4caf50;
}

.cca-status-box:nth-child(2) {
  background: #ff9800;
}

.cca-status-box:last-child {
  background: #f44336;
}

/* GHG Emissions Profile Styles */
.ghg-profile-content {
  display: flex;
  flex-direction: column;
  gap: 2rem;
  max-width: 1400px;
}

.ghg-metrics-section {
  margin-bottom: 1rem;
}

.ghg-metrics-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
}

.grouped-bar-chart {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.grouped-bar-item {
  display: flex;
  align-items: end;
  gap: 1rem;
}

.sector-label {
  min-width: 120px;
  font-size: 0.8rem;
  font-weight: 500;
  color: #333;
  text-align: right;
}

.bar-group {
  flex: 1;
  height: 120px;
  display: flex;
  align-items: end;
}

.bar-pair {
  display: flex;
  align-items: end;
  gap: 8px;
  width: 100%;
  max-width: 80px;
}

.emission-bar,
.reduction-bar {
  flex: 1;
  min-height: 4px;
  border-radius: 4px;
  position: relative;
  display: flex;
  align-items: end;
  justify-content: center;
  transition: height 0.6s ease;
}

.emission-bar {
  background: #0056b3;
}

.reduction-bar {
  background: #ff9800;
}

.emission-bar .bar-value,
.reduction-bar .bar-value {
  position: absolute;
  top: -20px;
  font-size: 0.7rem;
  color: #333;
  font-weight: 600;
  white-space: nowrap;
  transform: rotate(-45deg);
  transform-origin: bottom;
}

.grouped-chart-legend {
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin-top: 1rem;
}

.legend-dot.emission {
  background: #0056b3;
}

.legend-dot.reduction {
  background: #ff9800;
}

/* Responsive adjustments for new content */
@media (max-width: 768px) {
  .climate-resilience-content,
  .climate-risk-content,
  .ghg-profile-content {
    gap: 1rem;
  }
  
  .resilience-legend,
  .radar-legend {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .ghg-metrics-grid {
    grid-template-columns: 1fr;
  }
  
  .risk-categories {
    grid-template-columns: 1fr;
  }
  
  .cca-status {
    flex-direction: column;
  }
  
  .horizontal-bar-item,
  .ppp-sector-item {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
  
  .grouped-bar-item {
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
  }
  
  .sector-label {
    text-align: center;
    min-width: auto;
  }
}

/* Updated GHG Horizontal Chart Styles */
.ghg-horizontal-chart {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.ghg-chart-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.ghg-sector-row {
  display: grid;
  grid-template-columns: 200px 1fr;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem 0;
}

.ghg-sector-label {
  font-size: 0.9rem;
  font-weight: 500;
  color: #333;
  text-align: right;
}

.ghg-bars-container {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.ghg-bar-group {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.ghg-emission-bar,
.ghg-reduction-bar {
  height: 25px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 0.5rem;
  transition: width 0.6s ease;
  min-width: 30px;
}

.ghg-emission-bar {
  background: #0056b3;
}

.ghg-reduction-bar {
  background: #ff9800;
}

.ghg-bar-value {
  font-size: 0.8rem;
  color: white;
  font-weight: 600;
}

.ghg-chart-legend {
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin-top: 1rem;
}

/* Viability Analysis Styles */
.viability-analysis-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-width: 1400px;
}

.viability-sub-nav {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.08);
  padding: 1rem;
  margin-bottom: 1rem;
}

.sub-nav-tabs {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.sub-nav-tab {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border: none;
  background: #f5f5f5;
  color: #666;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
  font-weight: 500;
  white-space: nowrap;
}

.sub-nav-tab:hover {
  background: #e0e0e0;
  color: #333;
}

.sub-nav-tab.active {
  background: #0056b3;
  color: white;
}

.sub-nav-tab .tab-icon {
  font-size: 1rem;
}

.sub-nav-tab .tab-text {
  font-size: 0.9rem;
}

.viability-tab-content {
  min-height: 400px;
}

/* Ease of Implementation Styles */
.ease-implementation-tab {
  width: 100%;
}

.viability-legend {
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin-bottom: 2rem;
}

.viability-chart {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.viability-sector-row {
  display: grid;
  grid-template-columns: 200px 1fr 100px;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem 0;
}

.viability-sector-label {
  font-size: 0.9rem;
  font-weight: 500;
  color: #333;
  text-align: right;
}

.viability-bars {
  display: flex;
  height: 30px;
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid #e0e0e0;
}

.viability-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 0.8rem;
  transition: all 0.3s;
}

.viability-bar.high {
  background: #0056b3;
}

.viability-bar.med-high {
  background: #4caf50;
}

.viability-bar.medium {
  background: #fbc02d;
}

.viability-bar.med-low {
  background: #ff9800;
}

.viability-bar.low {
  background: #f44336;
}

.viability-count {
  color: white;
}

.viability-total {
  font-size: 0.8rem;
  font-weight: 600;
  color: #0056b3;
  text-align: center;
}

.legend-dot.high {
  background: #0056b3;
}

.legend-dot.med-high {
  background: #4caf50;
}

.legend-dot.medium {
  background: #fbc02d;
}

.legend-dot.med-low {
  background: #ff9800;
}

.legend-dot.low {
  background: #f44336;
}

/* E&S Sustainability Tab Styles */
.es-sustainability-tab {
  width: 100%;
}

.three-charts-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
}

.simple-viability-legend {
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.simple-viability-chart {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.simple-viability-row {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.simple-sector-label {
  font-size: 0.8rem;
  font-weight: 500;
  color: #333;
  min-width: 120px;
  text-align: right;
}

.simple-bars {
  flex: 1;
  display: flex;
}

.simple-bar {
  height: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 0.8rem;
  border-radius: 4px;
  padding: 0 0.5rem;
}

.simple-bar.high {
  background: #0056b3;
}

.simple-count {
  color: white;
}

/* Help Section Styles */
.help-tab {
  width: 100%;
}

.help-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
}

.help-content {
  text-align: center;
  max-width: 500px;
}

.help-content h4 {
  color: #0056b3;
  font-size: 1.5rem;
  margin-bottom: 1rem;
}

.help-content p {
  color: #666;
  line-height: 1.6;
  margin-bottom: 1rem;
}

.help-icon {
  font-size: 3rem;
  opacity: 0.3;
  margin: 1rem 0;
}

/* Responsive Design for Viability Analysis */
@media (max-width: 1200px) {
  .three-charts-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
}

@media (max-width: 768px) {
  .viability-sector-row {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
  
  .viability-sector-label {
    text-align: left;
  }
  
  .viability-legend,
  .simple-viability-legend {
    flex-wrap: wrap;
    gap: 1rem;
  }
  
  .sub-nav-tabs {
    flex-direction: column;
  }
  
  .sub-nav-tab {
    justify-content: center;
  }
  
  .simple-viability-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
  
  .simple-sector-label {
    min-width: auto;
    text-align: left;
  }
  
  .ghg-sector-row {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
  
  .ghg-sector-label {
    text-align: left;
  }
}
</style>